export const DEFAULT_RESERVED = Array.from({ length: 15 }, (_, i) => (i + 1) * 10);
export const SHEET_HEADERS = ['Tier', '#', 'ITEM DESCRIPTION', 'COMBINED', 'QTY', 'Cost per Item (£)', 'Opp. Cost (£)', 'Retail/Perceived (£)', 'Probability', 'Buyer EV (£)', 'Total Cost (£)'];

export function makeSetup({ title = 'Slab Surprise Set', totalSlots = 200, reserved = DEFAULT_RESERVED, perRow = 32 } = {}) {
  totalSlots = Number(totalSlots); perRow = Number(perRow);
  if (!Number.isInteger(totalSlots) || totalSlots < 1 || totalSlots > 500) throw new Error('Choose between 1 and 500 numbered slots.');
  if (!Number.isInteger(perRow) || perRow < 1 || perRow > 100) throw new Error('Choose between 1 and 100 slabs per storage row.');
  const values = Array.isArray(reserved) ? reserved : String(reserved).trim().split(/[,\s]+/).filter(Boolean);
  const numbers = values.map(Number);
  if (numbers.some(n => !Number.isInteger(n) || n < 1 || n > totalSlots) || new Set(numbers).size !== numbers.length) throw new Error('Wall numbers must be unique numbers within the set.');
  if (!String(title).trim() || String(title).length > 120) throw new Error('Give the set a name, up to 120 characters.');
  return { title: String(title).trim(), totalSlots, reserved: numbers.sort((a, b) => a - b), perRow };
}

export function newDraft(setup = makeSetup()) {
  return { version: 1, id: globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`, setup: makeSetup(setup), entries: [], paused: false };
}

export function slotsFor(setup) {
  const wall = new Set(setup.reserved);
  let boxed = 0;
  return Array.from({ length: setup.totalSlots }, (_, index) => {
    const number = index + 1;
    if (wall.has(number)) return { number, kind: 'wall', location: 'Front wall' };
    const ordinal = boxed++;
    const box = Math.floor(ordinal / (setup.perRow * 3)) + 1;
    const row = Math.floor(ordinal / setup.perRow) % 3 + 1;
    const position = ordinal % setup.perRow + 1;
    return { number, kind: 'boxed', box, row, position, location: `Box ${box} · row ${row} · ${position}` };
  });
}

export function parseCert(value) {
  const raw = String(value).trim();
  if (/^\d{4,12}$/.test(raw)) return raw;
  try {
    const url = new URL(raw);
    if (url.protocol === 'https:' && ['acegrading.com', 'www.acegrading.com'].includes(url.hostname) && !url.username && !url.password && !url.port) {
      return url.pathname.match(/^\/cert(?:ification)?\/(\d{4,12})\/?$/)?.[1] || null;
    }
  } catch { /* A plain invalid certificate. */ }
  return null;
}

export function addScans(draft, text, kind = 'boxed') {
  if (!['boxed', 'wall'].includes(kind)) throw new Error('Choose boxed slabs or front wall.');
  const tokens = String(text).trim().split(/[\s,;]+/).filter(Boolean);
  if (tokens.length > 500) throw new Error('Paste no more than 500 certificates at once.');
  const entries = [...draft.entries];
  const occupied = new Set(entries.map(e => e.number));
  const certs = new Set(entries.map(e => e.certNumber));
  const slots = slotsFor(draft.setup).filter(slot => slot.kind === kind && !occupied.has(slot.number));
  const report = { accepted: 0, duplicate: 0, invalid: 0, full: 0 };
  for (const token of tokens) {
    const certNumber = parseCert(token);
    if (!certNumber) { report.invalid++; continue; }
    if (certs.has(certNumber)) { report.duplicate++; continue; }
    const slot = slots.shift();
    if (!slot) { report.full++; continue; }
    certs.add(certNumber);
    entries.push({ number: slot.number, certNumber, status: 'queued', card: null, description: '', error: '' });
    report.accepted++;
  }
  return { draft: { ...draft, entries }, report };
}

function textField(value, limit = 500) {
  return typeof value === 'string' ? value.slice(0, limit).replace(/[\t\r\n\u0000-\u001f]/g, ' ').trim() : '';
}

export function validateCard(card, expectedCert) {
  if (!card || card.certNumber !== expectedCert || !/^\d{4,12}$/.test(expectedCert) || !textField(card.name) || !textField(card.setName)
    || !Number.isFinite(card.grade) || card.grade < 1 || card.grade > 10) throw new Error('ACE returned an incomplete or mismatched certificate. Retry this slot.');
  const result = { certNumber: expectedCert, grade: card.grade };
  for (const key of ['name', 'setName', 'number', 'language', 'variant', 'edition', 'qualifier', 'additionalInformation']) result[key] = textField(card[key]);
  for (const key of ['imageFront', 'imageBack']) {
    try { const url = new URL(card[key]); result[key] = url.protocol === 'https:' && !url.username && !url.password ? url.href : ''; }
    catch { result[key] = ''; }
  }
  return result;
}

export function cardDescription(card) {
  const name = textField(card.name);
  const extras = [card.variant, card.edition, card.qualifier].map(v => textField(v)).filter(v => v && !name.toLowerCase().includes(v.toLowerCase()));
  return `ACE ${card.grade} - ${[name, ...new Set(extras), card.number].filter(Boolean).join(' ')}`.toUpperCase();
}

export function resolveScan(draft, number, certNumber, card) {
  const clean = validateCard(card, certNumber);
  return { ...draft, entries: draft.entries.map(entry => entry.number === number && entry.certNumber === certNumber
    ? { ...entry, status: 'ready', card: clean, description: cardDescription(clean), error: '' } : entry) };
}

export function restoreDraft(raw) {
  if (!raw || raw.version !== 1 || typeof raw.id !== 'string' || !Array.isArray(raw.entries)) throw new Error('This is not a saved slab-surprise batch.');
  const setup = makeSetup(raw.setup);
  const entries = []; const numbers = new Set(); const certs = new Set();
  if (raw.entries.length > setup.totalSlots) throw new Error('The saved batch has too many slabs.');
  for (const row of raw.entries) {
    if (!Number.isInteger(row.number) || row.number < 1 || row.number > setup.totalSlots || numbers.has(row.number) || certs.has(row.certNumber)
      || !/^\d{4,12}$/.test(row.certNumber) || !['queued', 'loading', 'ready', 'error'].includes(row.status)) throw new Error('The saved batch contains invalid or duplicate slots.');
    numbers.add(row.number); certs.add(row.certNumber);
    const card = row.status === 'ready' ? validateCard(row.card, row.certNumber) : null;
    entries.push({ number: row.number, certNumber: row.certNumber, card, status: row.status === 'loading' ? 'queued' : row.status,
      description: card ? textField(row.description) || cardDescription(card) : '', error: textField(row.error) });
  }
  return { version: 1, id: raw.id, setup, entries, paused: raw.paused === true || entries.some(e => e.status === 'queued') };
}

// Escape untrusted text independently of the formulas this exporter generates.
export function sheetText(value) {
  const text = textField(String(value ?? ''), 2000);
  return /^[=+\-@]/.test(text) ? `'${text}` : text;
}

export function sheetRows(draft) {
  const byNumber = new Map(draft.entries.map(entry => [entry.number, entry]));
  const lastRow = draft.setup.totalSlots + 1;
  return [SHEET_HEADERS, ...slotsFor(draft.setup).map(slot => {
    const entry = byNumber.get(slot.number); const ready = entry?.status === 'ready'; const row = slot.number + 1;
    return ['', `#${slot.number}`, ready ? sheetText(entry.description) : '', `=IF(C${row}="","",B${row}&" "&C${row})`, ready ? '1' : '', '', '', '',
      `=IF(E${row}="","",E${row}/SUM($E$2:$E$${lastRow}))`, `=IF(G${row}="","",I${row}*G${row})`, `=IF(F${row}="","",E${row}*F${row})`];
  })];
}

export function tsv(rows) { return rows.map(row => row.join('\t')).join('\n'); }
export function csv(rows) { return '\uFEFF' + rows.map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(',')).join('\r\n'); }

export function existingSheetBlocks(draft) {
  const ready = draft.entries.filter(e => e.status === 'ready').sort((a, b) => a.number - b.number);
  const blocks = [];
  for (const entry of ready) {
    let block = blocks.at(-1);
    if (!block || entry.number !== block.end + 1) { block = { start: entry.number, end: entry.number, rows: [] }; blocks.push(block); }
    block.end = entry.number;
    block.rows.push([`#${entry.number}`, sheetText(entry.description)]);
  }
  return blocks.map(block => ({ ...block, range: `B${block.start + 1}:C${block.end + 1}`, text: tsv(block.rows) }));
}

export function auditRows(draft) {
  const byNumber = new Map(draft.entries.map(entry => [entry.number, entry]));
  return [['#', 'Certificate', 'Description', 'Set', 'Card number', 'Grade', 'Language', 'Variant', 'Edition', 'Qualifier', 'Location', 'Status'],
    ...slotsFor(draft.setup).filter(slot => byNumber.has(slot.number)).map(slot => {
      const row = byNumber.get(slot.number); const card = row.card || {};
      return [slot.number, row.certNumber, row.description, card.setName, card.number, card.grade, card.language, card.variant, card.edition, card.qualifier, slot.location, row.status].map(sheetText);
    })];
}

export function wheelText(draft) {
  if (draft.entries.length !== draft.setup.totalSlots || draft.entries.some(e => e.status !== 'ready')) throw new Error('Resolve every slot, including the wall, before copying the full set list.');
  return draft.entries.slice().sort((a, b) => a.number - b.number).map(e => `#${e.number} ${e.description}`).join('\n');
}
